import { fetch } from 'expo/fetch';
import { supabase } from '@/data/client';
import { ensureFreshSession } from '@/utils/authRefresh';

const SESSION_FIELDS = 'id, organizationID, creatorUserID, creatorPersonID, title, createdAt, updatedAt';

export class AssistantError extends Error {
    constructor(message, { status = 0, code = 'ASSISTANT_ERROR', retryable = false } = {}) {
        super(message);
        this.name = 'AssistantError';
        this.status = status;
        this.code = code;
        this.retryable = retryable;
    }
}

const throwDataError = (error) => {
    if (error) throw new AssistantError(error.message, { code: error.code });
};

export const getAssistantSessions = async ({ organizationID } = {}) => {
    if (!organizationID) return [];
    const { data, error } = await supabase
        .from('AssistantSessions')
        .select(SESSION_FIELDS)
        .eq('organizationID', organizationID)
        .order('updatedAt', { ascending: false });
    throwDataError(error);
    return data || [];
};

// organizationID stays in the signature for callers; the RPC scopes by
// p_session_id only and applies access checks server-side (restricted
// assistant messages come back content: '' + accessRestricted flag).
export const getAssistantMessages = async ({ organizationID, sessionID } = {}) => {
    if (!organizationID || !sessionID) return [];
    const { data, error } = await supabase.rpc('assistant_get_messages', { p_session_id: sessionID });
    throwDataError(error);
    return data || [];
};

export const deleteAssistantSession = async ({ organizationID, sessionID }) => {
    const { error } = await supabase
        .from('AssistantSessions')
        .delete()
        .eq('organizationID', organizationID)
        .eq('id', sessionID);
    throwDataError(error);
    return sessionID;
};

export const revertAssistantFromMessage = async ({ sessionID, userMessageID }) => {
    const { data, error } = await supabase.rpc('assistant_revert_from_message', {
        p_session_id: sessionID,
        p_user_message_id: userMessageID,
    });
    throwDataError(error);
    return data;
};

export const createSSEParser = (onEvent) => {
    let line = '';
    let eventName = 'message';
    let dataLines = [];
    let pendingCR = false;

    const dispatch = () => {
        if (!dataLines.length) {
            eventName = 'message';
            return;
        }
        const rawData = dataLines.join('\n');
        dataLines = [];
        const type = eventName || 'message';
        eventName = 'message';
        if (rawData === '[DONE]') {
            onEvent({ type: 'done', data: { status: 'completed' } });
            return;
        }
        let data;
        try {
            data = JSON.parse(rawData);
        } catch {
            data = { delta: rawData };
        }
        onEvent({ type: type === 'message' && data?.type ? data.type : type, data });
    };

    const processLine = () => {
        if (!line) {
            dispatch();
        } else if (!line.startsWith(':')) {
            const colon = line.indexOf(':');
            const field = colon < 0 ? line : line.slice(0, colon);
            let value = colon < 0 ? '' : line.slice(colon + 1);
            if (value.startsWith(' ')) value = value.slice(1);
            if (field === 'event') eventName = value;
            if (field === 'data') dataLines.push(value);
        }
        line = '';
    };

    const feed = (chunk) => {
        for (const char of chunk) {
            if (pendingCR) {
                pendingCR = false;
                processLine();
                if (char === '\n') continue;
            }
            if (char === '\r') pendingCR = true;
            else if (char === '\n') processLine();
            else line += char;
        }
    };

    const finish = () => {
        if (pendingCR) {
            pendingCR = false;
            processLine();
        }
        if (line) processLine();
        dispatch();
    };

    return { feed, finish };
};

const getAccessToken = async () => {
    let { data: { session } } = await supabase.auth.getSession();
    const secondsLeft = session?.expires_at ? session.expires_at - Date.now() / 1000 : 0;
    if (!session?.access_token || secondsLeft < 300) {
        const refreshed = await ensureFreshSession();
        session = refreshed?.data?.session;
    }
    if (!session?.access_token) throw new AssistantError('Your session expired. Sign in again.', { status: 401, code: 'UNAUTHORIZED' });
    return session.access_token;
};

const parseResponseError = async (response) => {
    let body = {};
    try {
        const text = await response.text();
        try {
            body = JSON.parse(text);
        } catch {
            body = { message: text };
        }
    } catch {
        body = {};
    }
    const fallback = response.status === 409
        ? 'This session is already generating a response.'
        : `Assistant request failed (${response.status}).`;
    throw new AssistantError(body.message || body.error?.message || body.error || fallback, {
        status: response.status,
        code: body.code || body.error?.code || (response.status === 409 ? 'SESSION_BUSY' : 'HTTP_ERROR'),
        retryable: body.retryable ?? response.status >= 500,
    });
};

const getClientContext = () => {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    let timeZone;
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch { /* Optional context only. */ }
    return {
        date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
        ...(timeZone ? { timeZone } : {}),
    };
};

export const streamAssistantMessage = async ({ sessionID, content, selection, signal, onEvent }) => {
    const accessToken = await getAccessToken();
    const baseUrl = process.env.EXPO_PUBLIC_DB_URL?.replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/functions/v1/assistant`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
        },
        body: JSON.stringify({
            action: 'send',
            ...(sessionID ? { sessionID } : {}),
            content,
            ...(sessionID && selection ? { selection } : {}),
            clientContext: getClientContext(),
        }),
        signal,
    });

    if (!response.ok) await parseResponseError(response);
    if (!response.body) throw new AssistantError('Streaming is unavailable on this device.', { code: 'STREAM_UNAVAILABLE', retryable: true });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = createSSEParser(onEvent);
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            parser.feed(typeof value === 'string' ? value : decoder.decode(value, { stream: true }));
        }
        parser.feed(decoder.decode());
        parser.finish();
    } finally {
        reader.releaseLock?.();
    }
};
